/** Kept clear of every viewport edge so a dialog never sits flush against the window. */
export const DIALOG_VIEWPORT_MARGIN_PX = 8;

/** Visible gap between a column's filter icon and the dialog it opens, leaving room for the arrow. */
export const DIALOG_ICON_GAP_PX = 8;

/**
 * Below this a dialog is not usable at all, so it slides up over its own icon rather than shrinking
 * further -- the case on a short laptop viewport at 200% zoom with the icon near the bottom. Set to
 * 380 (not an arbitrary floor) because the Excel filter dialog's own fixed chrome -- .e-contextmenu-wrapper
 * (the always-visible Sort/Clear Filter/Text Filters menu, measured live at ~149px) plus .e-footer-content
 * (OK/Cancel, ~80px) plus .e-searchbox with padding (~40px) -- already costs ~269px before any checklist
 * row is visible. 380px leaves roughly 100px of headroom for checklist rows to provide a genuinely usable
 * checkbox list (approximately 2-3 visible rows, tested live at 200% zoom); the grid dialog's own flex-chain
 * fix (reportsGridDialogStyles.tsx) makes the checklist scroll internally, rather than overflow past the
 * dialog, for the rarer cases where even that isn't enough room.
 */
export const MIN_DIALOG_HEIGHT_PX = 380;

/** The filter icon or toolbar button the dialog opens from, in viewport coordinates. */
export interface IDialogAnchorRect {
    topPx: number;
    leftPx: number;
    rightPx: number;
    bottomPx: number;
    widthPx: number;
}

export interface IDialogSize {
    widthPx: number;
    heightPx: number;
}

export interface IViewportBounds {
    widthPx: number;
    heightPx: number;
}

export interface IDialogViewportPosition {
    topPx: number;
    leftPx: number;
    maxHeightPx: number;
    /**
     * Which side of the icon the dialog hangs from: 'right' opens bottom-right (dialog left edge under the
     * icon's left edge), 'left' opens bottom-left. Surfaced so the caller can move the CSS arrow to match.
     */
    anchor: 'left' | 'right';
}

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

/**
 * Computes where a grid dialog should sit, in viewport coordinates, from the icon it opens under.
 *
 * Derived fresh from the anchor every time rather than adjusted from whatever `left` Syncfusion last
 * wrote: the dialog can change offset parent mid-life (see REPORTS_GRID_POPUP_ID), which silently changes
 * what a previously-written `left` means. Reading only the anchor's own viewport rect keeps this correct
 * in either home and idempotent when the same dialog is positioned twice.
 */
export const getDialogViewportPosition = (
    anchorRect: IDialogAnchorRect,
    dialogSize: IDialogSize,
    viewport: IViewportBounds
): IDialogViewportPosition => {
    const minLeftPx = DIALOG_VIEWPORT_MARGIN_PX;
    const maxLeftPx = viewport.widthPx - DIALOG_VIEWPORT_MARGIN_PX - dialogSize.widthPx;

    // Preferred: bottom-right of the icon, i.e. the dialog's left edge under the icon's left edge.
    const bottomRightLeftPx = anchorRect.leftPx;
    const opensBottomRight = bottomRightLeftPx <= maxLeftPx;
    const anchor: 'left' | 'right' = opensBottomRight ? 'right' : 'left';
    const preferredLeftPx = opensBottomRight ? bottomRightLeftPx : anchorRect.rightPx - dialogSize.widthPx;

    // maxLeftPx goes negative when the dialog is wider than the viewport allows; clamping low-to-high in
    // that order keeps the dialog's left edge on screen rather than pinning it off the left.
    const leftPx = clamp(preferredLeftPx, minLeftPx, Math.max(minLeftPx, maxLeftPx));

    const preferredTopPx = anchorRect.bottomPx + DIALOG_ICON_GAP_PX;
    const availableBelowPx = viewport.heightPx - DIALOG_VIEWPORT_MARGIN_PX - preferredTopPx;
    const maxHeightPx = Math.max(MIN_DIALOG_HEIGHT_PX, Math.min(dialogSize.heightPx, availableBelowPx));
    const highestTopPx = viewport.heightPx - DIALOG_VIEWPORT_MARGIN_PX - maxHeightPx;
    const topPx = clamp(preferredTopPx, DIALOG_VIEWPORT_MARGIN_PX, Math.max(DIALOG_VIEWPORT_MARGIN_PX, highestTopPx));

    return { topPx, leftPx, maxHeightPx, anchor };
};

/**
 * Converts a viewport position into coordinates for the dialog's current offset parent, which is what
 * `style.top` / `style.left` are actually resolved against.
 */
export const toOffsetParentCoords = (
    position: IDialogViewportPosition,
    offsetParentRect: { top: number; left: number }
): { topPx: number; leftPx: number } => ({
    topPx: position.topPx - offsetParentRect.top,
    leftPx: position.leftPx - offsetParentRect.left,
});
